import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  rem,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCoins,
  IconCheck,
  IconPlus,
  IconMinus,
  IconRefresh,
} from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(400),
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  headerIcon: {
    width: rem(48),
    height: rem(48),
    borderRadius: theme.radius.md,
    backgroundColor: `${theme.colors.amber[8]}20`,
    border: `1px solid ${theme.colors.amber[8]}40`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.amber[4],
  },
  headerTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  headerSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[3],
  },
  card: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  balanceCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
  },
  balanceLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.colors.dark[2],
  },
  balanceValue: {
    fontSize: rem(28),
    fontWeight: 700,
    color: theme.colors.amber[4],
  },
  balanceSymbol: {
    fontSize: theme.fontSizes.md,
    fontWeight: 500,
    color: theme.colors.dark[3],
    marginLeft: theme.spacing.xs,
  },
  operationCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
  },
  segmentedControl: {
    backgroundColor: theme.colors.dark[6],
  },
  previewCard: {
    backgroundColor: `${theme.colors.cyan[9]}15`,
    border: `1px solid ${theme.colors.cyan[7]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
  },
  previewLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[2],
  },
  previewValue: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 700,
  },
  previewPositive: {
    color: theme.colors.green[4],
  },
  previewNegative: {
    color: theme.colors.rose[4],
  },
  previewNeutral: {
    color: theme.colors.cyan[4],
  },
  submitButton: {
    fontWeight: 600,
    marginTop: theme.spacing.md,
  },
  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: rem(200),
  },
}));

const CURRENCIES_QUERY = gql`
  query CurrenciesForAdjustment {
    currencies(page: { first: 100 }) {
      nodes {
        id
        name
        symbol
        description
      }
    }
  }
`;

const PLAYER_CURRENCIES_QUERY = gql`
  query PlayerCurrencies($playerId: ID!) {
    playerCurrencies(
      filter: { playerProfileIds: [$playerId] }
      page: { first: 100 }
    ) {
      nodes {
        id
        balance
        currencyId
      }
    }
  }
`;

const UPDATE_PLAYER_CURRENCY_MUTATION = gql`
  mutation UpdatePlayerCurrency(
    $playerId: ID!
    $input: [PlayerCurrencyUpdateInput!]!
  ) {
    playerCurrencyUpdate(playerId: $playerId, input: $input) {
      id
      balance
      currencyId
    }
  }
`;

type OperationType = "add" | "remove" | "set";

interface CurrencyAdjustmentModalProps {
  playerId: string;
  playerName: string;
  onSuccess?: () => void;
}

export const CurrencyAdjustmentModal = ({
  playerId,
  playerName,
  onSuccess,
}: CurrencyAdjustmentModalProps) => {
  const { classes, cx } = useStyles();
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string | null>(
    null,
  );
  const [operation, setOperation] = useState<OperationType>("add");
  const [amount, setAmount] = useState<number | "">(0);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: currenciesData, loading: currenciesLoading } =
    useQuery<any>(CURRENCIES_QUERY);
  const { data: playerCurrenciesData, loading: playerCurrenciesLoading } =
    useQuery<any>(PLAYER_CURRENCIES_QUERY, {
      variables: { playerId },
      skip: !playerId,
    });

  const [updateCurrency] = useMutation<any>(UPDATE_PLAYER_CURRENCY_MUTATION);

  const currencies = currenciesData?.currencies?.nodes || [];
  const playerCurrencies = playerCurrenciesData?.playerCurrencies?.nodes || [];

  const selectedCurrency = currencies.find(
    (c: any) => c.id === selectedCurrencyId,
  );
  const playerBalance =
    playerCurrencies.find((pc: any) => pc.currencyId === selectedCurrencyId)
      ?.balance || 0;

  const calculateNewBalance = () => {
    const numAmount = typeof amount === "number" ? amount : 0;
    switch (operation) {
      case "add":
        return playerBalance + numAmount;
      case "remove":
        return Math.max(0, playerBalance - numAmount);
      case "set":
        return numAmount;
      default:
        return playerBalance;
    }
  };

  const newBalance = calculateNewBalance();
  const balanceDiff = newBalance - playerBalance;

  const handleSubmit = async () => {
    if (!selectedCurrencyId || amount === "" || amount === 0) {
      notifications.show({
        title: "Invalid Input",
        message: "Please select a currency and enter an amount",
        color: "rose",
      });
      return;
    }

    setSubmitting(true);
    try {
      const numAmount = typeof amount === "number" ? amount : 0;
      const input: any = {
        items: [
          {
            currencyId: selectedCurrencyId,
            ...(operation === "add" && { add: numAmount }),
            ...(operation === "remove" && { remove: numAmount }),
            ...(operation === "set" && { forceBalance: numAmount }),
          },
        ],
      };

      const result = await updateCurrency({
        variables: {
          playerId,
          input: [input],
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Currency Updated",
        message: `${selectedCurrency?.name} balance updated for ${playerName}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onSuccess?.();
      modals.closeAll();
    } catch (error) {
      notifications.show({
        title: "Update Failed",
        message:
          error instanceof Error ? error.message : "Failed to update currency",
        color: "rose",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (currenciesLoading || playerCurrenciesLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <Loader color="amber" />
      </Box>
    );
  }

  const currencyOptions = currencies.map((currency: any) => ({
    value: currency.id,
    label: `${currency.name} (${currency.symbol})`,
  }));

  return (
    <Stack className={classes.container}>
      {/* Header */}
      <Group className={classes.header}>
        <Box className={classes.headerIcon}>
          <IconCoins size={24} />
        </Box>
        <div>
          <Text className={classes.headerTitle}>Adjust Currency</Text>
          <Text className={classes.headerSubtitle}>
            Modify currency balance for {playerName}
          </Text>
        </div>
      </Group>

      {/* Currency Selection */}
      <Card className={classes.card}>
        <Select
          label="Select Currency"
          placeholder="Choose a currency to adjust"
          data={currencyOptions}
          value={selectedCurrencyId}
          onChange={setSelectedCurrencyId}
          size="md"
          searchable
        />

        {/* Current Balance */}
        {selectedCurrencyId && (
          <Box className={classes.balanceCard} mt="md">
            <Text className={classes.balanceLabel}>Current Balance</Text>
            <Group gap={0} align="baseline">
              <Text className={classes.balanceValue}>
                {playerBalance.toLocaleString()}
              </Text>
              <Text className={classes.balanceSymbol}>
                {selectedCurrency?.symbol}
              </Text>
            </Group>
          </Box>
        )}
      </Card>

      {/* Operation Selection */}
      {selectedCurrencyId && (
        <Card className={classes.card}>
          <Text size="sm" fw={600} mb="xs" c="dimmed">
            Operation Type
          </Text>
          <SegmentedControl
            fullWidth
            className={classes.segmentedControl}
            value={operation}
            onChange={(value) => setOperation(value as OperationType)}
            data={[
              {
                value: "add",
                label: (
                  <Group gap={4} justify="center">
                    <IconPlus size={14} />
                    <span>Add</span>
                  </Group>
                ),
              },
              {
                value: "remove",
                label: (
                  <Group gap={4} justify="center">
                    <IconMinus size={14} />
                    <span>Remove</span>
                  </Group>
                ),
              },
              {
                value: "set",
                label: (
                  <Group gap={4} justify="center">
                    <IconRefresh size={14} />
                    <span>Set</span>
                  </Group>
                ),
              },
            ]}
          />

          <NumberInput
            label="Amount"
            placeholder="Enter amount"
            value={amount}
            onChange={(val) => setAmount(typeof val === "number" ? val : "")}
            min={0}
            size="md"
            mt="md"
            leftSection={
              operation === "add" ? (
                <IconPlus size={16} />
              ) : operation === "remove" ? (
                <IconMinus size={16} />
              ) : (
                <IconRefresh size={16} />
              )
            }
          />

          <Textarea
            label="Reason (optional)"
            placeholder="Enter a reason for this adjustment..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            mt="md"
            minRows={2}
          />
        </Card>
      )}

      {/* Preview */}
      {selectedCurrencyId && amount !== "" && amount !== 0 && (
        <Box className={classes.previewCard}>
          <Group justify="space-between">
            <div>
              <Text className={classes.previewLabel}>New Balance</Text>
              <Text
                className={cx(
                  classes.previewValue,
                  balanceDiff > 0
                    ? classes.previewPositive
                    : balanceDiff < 0
                      ? classes.previewNegative
                      : classes.previewNeutral,
                )}
              >
                {newBalance.toLocaleString()} {selectedCurrency?.symbol}
              </Text>
            </div>
            <div style={{ textAlign: "right" }}>
              <Text className={classes.previewLabel}>Change</Text>
              <Text
                className={cx(
                  classes.previewValue,
                  balanceDiff > 0
                    ? classes.previewPositive
                    : balanceDiff < 0
                      ? classes.previewNegative
                      : classes.previewNeutral,
                )}
              >
                {balanceDiff > 0 ? "+" : ""}
                {balanceDiff.toLocaleString()}
              </Text>
            </div>
          </Group>
        </Box>
      )}

      {/* Submit Button */}
      <Button
        className={classes.submitButton}
        disabled={!selectedCurrencyId || amount === "" || amount === 0}
        loading={submitting}
        onClick={handleSubmit}
        leftSection={<IconCheck size={16} />}
        fullWidth
      >
        Apply Currency Adjustment
      </Button>
    </Stack>
  );
};

// Helper to open the modal
export const openCurrencyAdjustmentModal = (
  props: CurrencyAdjustmentModalProps,
) => {
  modals.open({
    title: null,
    centered: true,
    size: "md",
    children: <CurrencyAdjustmentModal {...props} />,
  });
};
